import { useEffect, useState,type ReactNode } from 'react';
import { AlertCircle, ChevronDown, ChevronUp, Package } from 'lucide-react';

export type SolarPanelSummary = {
  solar_module_brand: string | null;
  solar_module_model: string | null;
  solar_module_max_module_power: number | null;
  solar_module_efficiency: number | null;
  solar_module_temperature_coefficient: number | null;
  module_quantity: number | null;
};

export default function SolarPanelInfoCard({
  title,
  data,
  loading = false,
  action,
  collapsible = true,
}: {
  title: string;
  data: SolarPanelSummary[] | null;
  loading?: boolean;
  action?: ReactNode;
  collapsible?: boolean;
}) {
  const [open, setOpen] = useState(Boolean(data?.length));

  useEffect(() => {
    setOpen(Boolean(data?.length));
  }, [data]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-2">{collapsible ? <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className="flex flex-1 items-center justify-between gap-3 rounded-xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-200"
      >
        <span>{title}</span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button> : <h3 className="flex-1 text-sm font-semibold text-slate-700">{title}</h3>}{action}</div>

      {(!collapsible || open) ? (
        <div className="pt-4">
          {loading ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-400">
              Yukleniyor...
            </div>
          ) : !data || data.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 bg-slate-50 p-6 text-center text-slate-500">
              <AlertCircle className="h-6 w-6 text-slate-400" />
              <p className="text-sm font-medium">No solar module data available</p>
            </div>
          ) : (
            <div className="flex flex-col gap-4 text-sm">
              {data.length > 1 && (
                <div className="flex items-center justify-between rounded-xl bg-blue-50 px-4 py-3">
                  <span className="font-semibold text-slate-700">Toplam Panel</span>
                  <span className="text-lg font-bold text-blue-700">{data.reduce((sum, panel) => sum + (panel.module_quantity ?? 0), 0)}</span>
                </div>
              )}
              {data.map((panel) => <div key={`${panel.solar_module_brand}-${panel.solar_module_model}`} className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
              <div className="flex-1 space-y-2 rounded-xl bg-slate-50 px-4 py-3 shadow-sm">
                <p>
                  <span className="font-semibold">Brand:</span>{' '}
                  {panel.solar_module_brand ?? '--'}
                </p>
                <p>
                  <span className="font-semibold">Model:</span>{' '}
                  {panel.solar_module_model ?? '--'}
                </p>
                <p>
                  <span className="font-semibold">Max Power:</span>{' '}
                  {panel.solar_module_max_module_power ?? '--'} W
                </p>
                <p>
                  <span className="font-semibold">Efficiency:</span>{' '}
                  {panel.solar_module_efficiency ?? '--'} %
                </p>
                <p>
                  <span className="font-semibold">Temp. Coeff:</span>{' '}
                  {panel.solar_module_temperature_coefficient ?? '--'} % / °C
                </p>
              </div>

              <div className="flex flex-1 flex-col items-center justify-center rounded-xl bg-blue-50 px-6 py-4 shadow-sm">
                <Package className="mb-2 text-blue-600" size={28} />
                <p className="text-lg font-bold text-blue-700">{panel.module_quantity ?? '--'}</p>
                <p className="text-xs text-slate-500">Modules</p>
              </div>
              </div>)}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
